/**
 * The vocabulary the document library shares between its list, its folders and its rows.
 *
 * DOC1c - the KINDS. ⭐ The vocabulary is the MEASURED one, in measured order (2026-09-05 live):
 * offering_memorandum 35, comp 26, title_report 5(+3 from the legacy enum), property_profile 2,
 * then the deal documents. ⛔ `none` is a real bucket - 25 files, being 16 portal-only and 9
 * filed nowhere.
 *
 * FV (2026-09-08): `brochure` and `listing_proposal` are the categories the generators WRITE
 * (`dealBrochureService`, `dealProposalService`) and nothing has filed yet - measured 0 each. His
 * folders are named "like properties or listings or proposals", so they are real folders that
 * happen to be empty today, and the page says "none yet" rather than hiding them.
 */
export const KINDS: Array<{ key: string; label: string; one?: string }> = [
  { key: 'offering_memorandum', label: 'Offering memoranda', one: 'Offering memorandum' },
  { key: 'comp', label: 'Comps', one: 'Comp' },
  { key: 'title_report', label: 'Title reports', one: 'Title report' },
  { key: 'property_profile', label: 'Property profiles', one: 'Property profile' },
  { key: 'contract', label: 'Contracts', one: 'Contract' },
  { key: 'loi', label: 'LOIs', one: 'LOI' },
  { key: 'psa', label: 'PSAs', one: 'PSA' },
  { key: 'listing_proposal', label: 'Listing proposals', one: 'Listing proposal' },
  /**
   * LP1.5 (2026-09-19). ⛔ NOT `lease`: that is a signed lease and this is a PROPOSAL to lease -
   * the tenant-side letter proposing terms to a landlord. This repo has paid for that conflation
   * once already ("a lease proposal is not the app's proposal"), and a folder that mixes executed
   * leases with offers is the same mistake in the place he browses.
   * ⭐ Listed while empty, for the FV ruling: his folders are real folders that say "none yet".
   */
  { key: 'lease_proposal', label: 'Lease proposals', one: 'Lease proposal' },
  // LD1 (the 120th, his question): a SIGNED lease or amendment, and a signed listing agreement - filed on the building and
  // the renewal deal. ⛔ Not `lease_proposal` (an offer); listed while empty, for the FV ruling.
  { key: 'lease', label: 'Leases', one: 'Lease' },
  { key: 'listing_agreement', label: 'Listing agreements', one: 'Listing agreement' },
  { key: 'brochure', label: 'Brochures', one: 'Brochure' },
  // WT1: a walk-through report filed on its property (photos inside). Its own kind - a record of a visit, not
  // a marketing piece - so it gets its own folder and badge.
  { key: 'tour_report', label: 'Walk-through reports', one: 'Walk-through report' },
  // ⭐ `other` is a REAL stored value (2 files, measured live). Without a chip for it those two
  // sit outside every option while the row implies the vocabulary is complete.
  { key: 'other', label: 'Other', one: 'Other' },
  // No singular: nothing carries `none` as a type, so no row ever renders it as a badge.
  { key: 'none', label: 'No type yet' },
];

/**
 * `offering_memorandum` -> `Offering memorandum`, for the row badge.
 *
 * ⭐ The singular is WRITTEN OUT rather than derived. A rule that trims a trailing "s" and turns
 * "a" into "um" gets `Offering memoranda` right by luck and would mangle the next word added; the
 * fallback below already handles anything not in the list.
 */
export const prettyKind = (k: string): string =>
  KINDS.find((x) => x.key === k)?.one
  ?? k.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** The plural, for a folder name: `comp` -> `Comps`. */
export const kindFolderLabel = (k: string): string =>
  KINDS.find((x) => x.key === k)?.label ?? prettyKind(k);

export type LibraryPlace = 'property' | 'master_property' | 'deal' | 'portal' | 'contact' | 'company';

/**
 * The places a document can be filed, as the folder view names them. The order is the order of
 * the top level; `contact` and `company` hold nothing today (measured) and appear only if they
 * ever do.
 */
export const PLACES: Array<{ key: LibraryPlace; label: string; always: boolean }> = [
  { key: 'property', label: 'Properties', always: true },
  { key: 'master_property', label: 'Property Database', always: true },
  { key: 'deal', label: 'Deals', always: true },
  { key: 'portal', label: 'Client portals', always: true },
  { key: 'contact', label: 'Contacts', always: false },
  { key: 'company', label: 'Companies', always: false },
];

export const placeLabel = (p: string): string => PLACES.find((x) => x.key === p)?.label ?? p;

/** "7 documents" / "1 document" / "none yet" - a count that names what it counts. */
export const countLabel = (n: number, noun = 'document'): string =>
  n === 0 ? 'none yet' : `${n} ${noun}${n === 1 ? '' : 's'}`;
